import type {
  BoxLabels,
  Branded,
  EdgeId,
  EdgeRecord,
  NodeData,
  NodeId,
  NodeRecord,
  NodeSn,
  Point,
} from '@ipfabric/utils';
import { maybePropOf, prop } from '@ipfabric/utils/fp/prop';
import { readonlyArray, set } from 'fp-ts';
import { identity, pipe } from 'fp-ts/lib/function';

export type NodesEdgesMap = { [Key in NodeId]: Set<EdgeId> };

/**
 * Alias of Node ID and Node SN for collapse groups
 *
 * After some experiments I ended up with this solution - custom brand
 * in intersection with NodeId and NodeSn. Reasoning:
 * * Unioning just NodeId & NodeSn led to various issues with generic containers
 *   like `Record<CollapsedNodeGroupId, *>` or `Set<CollapsedNodeGroupId>`.
 *   This way it works with minimal extra type transforming hassle.
 * * This way it expresses real relations best - every `CollapsedNodeGroupId` is
 *   also both `NodeSn` and `NodeId`, but not all `NodeSn`s and `NodeId`s are `CollapsedNodeGroupId`.
 */
export type CollapsedNodeGroupId = Branded<string, 'CollapsedNodeGroupId'> &
  NodeId &
  NodeSn;

export const collapsedNodeGroupIdFromNode: (
  nodeIdOrSn: NodeId | NodeSn,
) => CollapsedNodeGroupId = identity as any;

export type HydratedCollapsedNodeGroup = {
  /**
   * These are edges that were originally connected to nodes inside the collapsed group:
   * - Edges with both ends and also edges with only one end inside given group are present here.
   * - Only original edges that existed in the graph before any collapsing are stored here.
   *   When collapsing is performed related to already collapsed node, original edges are found
   *   and stored there rather than their collapsed-grouped replacements.
   */
  edges: EdgeRecord;
  id: CollapsedNodeGroupId;
  nodes: NodeRecord;
  /**
   * We store initial position of the collapse cloud along with collapsed nodes with their positions
   * to maintain their relative position to the collapse cloud.
   * This is used on uncollapse - all uncollapsed nodes' positions are set relatively to their original
   * position to the cloud, so the uncollapsed subgraph has the same position structure as it was
   * before collapse.
   */
  originalPosition: Point;
};

export type HydratedCollapsedNodeGroups = Record<
  CollapsedNodeGroupId,
  HydratedCollapsedNodeGroup
>;

export const isCollapsedNodeId =
  (collapsedNodeGroups: HydratedCollapsedNodeGroups) =>
  (nodeId: NodeId): nodeId is NodeId & CollapsedNodeGroupId =>
    collapsedNodeGroups[collapsedNodeGroupIdFromNode(nodeId)] !== undefined;

export interface GraphData {
  boxLabels: BoxLabels;
  collapsedNodeGroups: HydratedCollapsedNodeGroups;
  edges: EdgeRecord;
  nodes: NodeRecord;
  selectedNodeIds: Set<NodeId>;
  nodesEdgesMap: NodesEdgesMap;
  hiddenNodes: Set<NodeId>;
}

export const emptyGraphData: GraphData = {
  boxLabels: {},
  collapsedNodeGroups: {},
  edges: {},
  nodes: {},
  selectedNodeIds: set.empty,
  nodesEdgesMap: {},
  hiddenNodes: set.empty,
};

export const nodesDataFromIds =
  (nodeRecord: NodeRecord) =>
  (nodeIds: readonly NodeId[]): readonly NodeData[] =>
    pipe(nodeIds, readonlyArray.filterMap(maybePropOf(nodeRecord)));

export const nodesDataFromParentIds =
  (nodeRecord: NodeRecord) =>
  (nodesData: readonly NodeData[]): readonly NodeData[] =>
    pipe(
      nodesData,
      readonlyArray.map(prop('children')),
      readonlyArray.flatten,
      readonlyArray.filterMap(maybePropOf(nodeRecord)),
    );

export function getSelectedNodes(graph: GraphData): readonly NodeData[] {
  return pipe(graph.selectedNodeIds, Array.from, nodesDataFromIds(graph.nodes));
}
